import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import styled from "styled-components";
import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

// Register Chart.js modules
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, Title, Tooltip, Legend);

// Styled wrapper for chart container
const ChartWrapper = styled.div`
  position: relative;
  background-color: #f9f9f9;
  width: 100%;
  height: 40vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const WeekComparisonChart = ({ data }) => {
  const [chartData, setChartData] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);

  const obliczSprzedaz = (row) =>
    Math.max(
      0,
      parseFloat(
        row.K_15 || row.K_19 || row.K_10 || row.K_21 || row.K_17 || row.K_11 || row.K_22
      ) || 0
    );

  const getWeekNumber = (date) => {
    const currentDate = new Date(date);
    const firstDayOfYear = new Date(currentDate.getFullYear(), 0, 1);
    const pastDaysOfYear = (currentDate - firstDayOfYear) / (24 * 60 * 60 * 1000);
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };

  useEffect(() => {
    const years = new Set();
    data.forEach((row) => {
      const year = parseInt(row.Rok, 10);
      if (!Number.isNaN(year)) years.add(year);
    });

    const sortedYears = Array.from(years).sort((a, b) => a - b);
    setAvailableYears(sortedYears);
    if (sortedYears.length > 0) {
      setSelectedYear(sortedYears[sortedYears.length - 1]);
    }
  }, [data]);

  useEffect(() => {
    if (!selectedYear) return;

    const currentYear = selectedYear;
    const previousYear = selectedYear - 1;

    const weeklySales = { [currentYear]: {}, [previousYear]: {} };

    data.forEach((row) => {
      const dateStr = row.DataWystawienia;
      const year = parseInt(row.Rok, 10);
      if (![currentYear, previousYear].includes(year)) return;

      const week = getWeekNumber(dateStr);
      const sales = obliczSprzedaz(row);
      if (!weeklySales[year][week]) weeklySales[year][week] = 0;
      weeklySales[year][week] += sales;
    });

    const weeks = Array.from({ length: 52 }, (_, i) => i + 1);
    const currentSales = weeks.map((w) => weeklySales[currentYear][w] || 0);
    const previousSales = weeks.map((w) => weeklySales[previousYear][w] || 0);

    // Colors per your convention (no red max highlight)
    const GREEN = "#2e7d32";   // current-year bars & line
    const ORANGE = "#fb8c00";  // previous-year line

    setChartData({
      labels: weeks.map((w) => `Tydzień ${w}`),
      datasets: [
        {
          label: `Sprzedaż (${currentYear})`,
          data: currentSales,
          type: "bar",
          backgroundColor: "rgba(46, 125, 50, 0.70)",
          borderColor: GREEN,
          borderWidth: 1,
        },
        {
          label: `Trend (${currentYear})`,
          data: currentSales,
          type: "line",
          borderColor: GREEN,                 // current year line = green
          backgroundColor: "rgba(46,125,50,0.15)",
          fill: false,
          tension: 0.3,
          pointBackgroundColor: GREEN,
        },
        {
          label: `Trend (${previousYear})`,
          data: previousSales,
          type: "line",
          borderColor: ORANGE,                // previous year line = orange
          backgroundColor: "rgba(251,140,0,0.15)",
          fill: false,
          tension: 0.3,
          pointBackgroundColor: ORANGE,
        },
      ],
    });
  }, [data, selectedYear]);

  const handleYearChange = (e) => setSelectedYear(parseInt(e.target.value, 10));

  if (!availableYears.length) return <p>Ładowanie filtrów...</p>;
  if (!chartData) return <p>Ładowanie danych...</p>;

  return (
    <ChartWrapper>
      {/* Info Icon & Tooltip */}
      <InfoIconWrapper data-tooltip-id="week-comparison-tooltip">
        <Info />
      </InfoIconWrapper>

      <InfoTooltip
        id="week-comparison-tooltip"
        place="left"
        content={
          <>
            <strong>Sprzedaż tygodniowa (52 tyg.)</strong>
            <br />
            Ten wykres pokazuje wartości sprzedaży <br />
            w kolejnych tygodniach dla roku {selectedYear} oraz {selectedYear - 1}.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label htmlFor="year-select">Rok:</label>
          <select id="year-select" value={selectedYear} onChange={handleYearChange}>
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      <Bar
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: "Sprzedaż",
              },
              ticks: {
                callback: function (value) {
                  return Number(value)
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
                },
              },
            },
          },
          plugins: {
            title: {
              display: true,
              text: `Sprzedaż tygodniowa w roku (${selectedYear})`,
            },
            legend: {
              display: true,
              onClick: (e, legendItem, legend) => {
                const chart = legend.chart;
                const index = legendItem.datasetIndex;
                if (chart.isDatasetVisible(index)) {
                  chart.hide(index);
                } else {
                  chart.show(index);
                }
                chart.update();
              },
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  const label = context.dataset.label || "";
                  const value = context.parsed.y || 0;
                  const formattedValue = Number(value)
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
                  return `${label}: ${formattedValue}`;
                },
              },
            },
          },
        }}
      />
    </ChartWrapper>
  );
};

export default WeekComparisonChart;
